import React, { useMemo } from 'react'
import { Box, BoxProps } from '@ui'

import * as styles from './SoundWaveIcon.css'

type Props = {
    /** Whether to animate the bars */
    animate?: boolean
    /** Size of the icon container */
    size?: number
    /** Variant style: default (green bg, white bars) or inverted (no bg, green bars) */
    variant?: 'default' | 'inverted'
    /** Connection status - affects color and animation style */
    status?: 'connected' | 'connecting' | 'error'
}

/**
 * Animated sound wave icon with three vertical bars.
 * Used to indicate when a user is live in a channel.
 */
export const SoundWaveIcon = React.memo(function SoundWaveIcon(props: Props) {
    const { animate = true, size = 32, variant = 'default', status = 'connected' } = props

    const isInverted = variant === 'inverted'
    const isError = status === 'error'
    const isConnecting = status === 'connecting'

    // Determine colors based on status
    const { barColor, bgColor } = useMemo((): {
        barColor: BoxProps['background']
        bgColor: BoxProps['background']
    } => {
        if (isError) {
            return {
                barColor: isInverted ? 'negative' : 'level2',
                bgColor: isInverted ? undefined : 'negative',
            }
        }
        return {
            barColor: isInverted ? 'positive' : 'level2',
            bgColor: isInverted ? undefined : 'positive',
        }
    }, [isError, isInverted])

    // Determine animation classes based on status
    const getBarClass = (barIndex: 1 | 2 | 3) => {
        if (!animate) {
            return undefined
        }
        if (isConnecting) {
            // Sequential 1, 2, 3 animation for connecting
            switch (barIndex) {
                case 1:
                    return styles.connectingBar1Class
                case 2:
                    return styles.connectingBar2Class
                case 3:
                    return styles.connectingBar3Class
            }
        }
        // Normal wave animation for connected/error
        switch (barIndex) {
            case 1:
                return styles.bar1Class
            case 2:
                return styles.bar2Class
            case 3:
                return styles.bar3Class
        }
    }

    return (
        <Box centerContent background={bgColor} rounded="sm" style={{ width: size, height: size }}>
            <Box horizontal gap="xs" alignItems="center" style={{ height: size * 0.5 }}>
                <Box
                    background={barColor}
                    rounded="full"
                    className={getBarClass(1)}
                    style={{
                        width: 2,
                        height: '60%',
                        minHeight: 4,
                    }}
                />
                <Box
                    background={barColor}
                    rounded="full"
                    className={getBarClass(2)}
                    style={{
                        width: 2,
                        height: '100%',
                        minHeight: 4,
                    }}
                />
                <Box
                    background={barColor}
                    rounded="full"
                    className={getBarClass(3)}
                    style={{
                        width: 2,
                        height: '60%',
                        minHeight: 4,
                    }}
                />
            </Box>
        </Box>
    )
})

export const SoundWaveBars = React.memo(function SoundWaveBars(props: {
    animate?: boolean
    size?: number
    color?: BoxProps['background']
}) {
    const { animate = true, size = 32, color: barColor = 'level2' } = props
    const barWidth = size * 0.1

    return (
        <Box horizontal alignItems="center" style={{ gap: size * 0.08, height: size * 0.5 }}>
            <Box
                background={barColor}
                rounded="full"
                className={animate ? styles.bar1Class : undefined}
                style={{
                    width: barWidth,
                    height: '60%',
                    minHeight: 4,
                }}
            />
            <Box
                background={barColor}
                rounded="full"
                className={animate ? styles.bar2Class : undefined}
                style={{
                    width: barWidth,
                    height: '100%',
                    minHeight: 4,
                }}
            />
            <Box
                background={barColor}
                rounded="full"
                className={animate ? styles.bar3Class : undefined}
                style={{
                    width: barWidth,
                    height: '60%',
                    minHeight: 4,
                }}
            />
        </Box>
    )
})
